import type { PricingCampaignModel } from '@/shared/generated'
import type { GlobalPcSetsWorkspaceItemPanel } from '../../model/types'

import { useMemo, useState } from 'react'
import { find, flatten, map, pipe, prop, propEq } from 'lodash/fp'
import {
	ChevronsUpDownIcon,
	LoaderCircleIcon,
	PenIcon,
	XIcon,
} from 'lucide-react'
import { useTranslation } from 'react-i18next'

import CheckWaveIcon from '@/shared/components/icons/CheckWaveIcon'
import {
	Command,
	CommandEmpty,
	CommandGroup,
	CommandInput,
	CommandItem,
	CommandList,
} from '@/shared/components/ui/command'
import {
	InputBase,
	InputBaseAdornment,
	InputBaseAdornmentButton,
	InputBaseFlexWrapper,
} from '@/shared/components/ui/input-base'
import {
	Popover,
	PopoverContent,
	PopoverTrigger,
} from '@/shared/components/ui/popover'
import {
	Tooltip,
	TooltipContent,
	TooltipTrigger,
} from '@/shared/components/ui/tooltip'
import {
	Virtualized,
	VirtualizedVirtualizer,
} from '@/shared/components/ui/virtualizer'
import { ampli } from '@/shared/lib/ampli'
import { cn } from '@/shared/lib/utils'

import { useGlobalPcSets, usePanelController } from '../GlobalPcSetsProvider'
import { EditPricingCampaign } from './pricing-campaign/EditPricingCampaign'

const getPricingCampaigns = pipe(map(prop(['pricingCampaigns'])), flatten)

export const GlobalPcSetToolbar = ({
	panel: { id },
	className,
}: {
	panel: GlobalPcSetsWorkspaceItemPanel
	className?: string
}) => {
	const { t } = useTranslation()

	const [open, setOpen] = useState(false)
	const { isLoading, globalPcSets } = useGlobalPcSets()
	const { updatePanel, panel, removePanel } = usePanelController(id)
	const pricingCampaigns = useMemo(
		() => getPricingCampaigns(globalPcSets),
		[globalPcSets]
	)
	const selectedPricingCampaign = useMemo(
		() => pricingCampaigns?.find(({ id }) => id === panel?.pricingCampaignId),
		[pricingCampaigns, panel?.pricingCampaignId]
	)
	const selectedGlobalPcSet = find(
		propEq('id', selectedPricingCampaign?.global_pc_set_id),
		globalPcSets
	)
	const handleSelectPricingCampaign = (pricingCampaignId: string) => {
		updatePanel({ pricingCampaignId })
		setOpen(false)
	}
	const handleRemovePanel = () => {
		ampli.globalPcSetComparisonActionClicked({
			global_pc_set_comparison_action: 'remove_panel',
		})
		removePanel()
	}

	return (
		<InputBase
			role='combobox'
			className={cn('w-full', className)}
			disabled={isLoading}
		>
			<Popover open={open} onOpenChange={setOpen}>
				<PopoverTrigger
					disabled={isLoading}
					data-testid='scenario-toolbar-trigger'
					className='flex min-w-0 flex-1 cursor-pointer items-center justify-between'
				>
					<InputBaseFlexWrapper
						className={cn(
							'min-w-0 gap-2',
							!panel?.pricingCampaignId && 'text-placeholder'
						)}
					>
						{selectedPricingCampaign ? (
							<div className='flex h-full min-w-0 place-items-center gap-2'>
								<Tooltip>
									<TooltipTrigger asChild>
										<span className='block truncate text-left'>
											<b className='font-semibold'>
												{selectedGlobalPcSet?.name}
											</b>
											<span className='mx-1 text-muted-foreground'>-</span>
											{selectedPricingCampaign.name}
										</span>
									</TooltipTrigger>
									<TooltipContent>{`${selectedGlobalPcSet?.name} - ${selectedPricingCampaign.name}`}</TooltipContent>
								</Tooltip>
								{selectedGlobalPcSet?.is_main && (
									<Tooltip>
										<TooltipTrigger asChild>
											<CheckWaveIcon className='shrink-0' />
										</TooltipTrigger>
										<TooltipContent>{t('app.live')}</TooltipContent>
									</Tooltip>
								)}
							</div>
						) : (
							<span className='truncate'>
								{t('pc.select.placeholder', {
									defaultValue: 'Select pricing campaign',
								})}
							</span>
						)}
					</InputBaseFlexWrapper>
					<InputBaseAdornment>
						<ChevronsUpDownIcon className='size-4 text-muted-foreground opacity-50' />
					</InputBaseAdornment>
				</PopoverTrigger>
				<PopoverContent className='min-w-(--radix-popover-trigger-width) p-0'>
					<Command>
						<CommandInput placeholder={t('pc_name.placeholder')} />
						<Virtualized asChild>
							<CommandList>
								{!isLoading && (
									<CommandEmpty>
										{t('pricing_campaigns_list_is_empty')}
									</CommandEmpty>
								)}
								{isLoading ? (
									<div className='flex items-center justify-center py-4'>
										<LoaderCircleIcon className='size-4 animate-spin opacity-50' />
									</div>
								) : (
									<CommandGroup>
										<VirtualizedVirtualizer>
											{pricingCampaigns?.map((pricingCampaign) => (
												<PricingCampaignItem
													key={pricingCampaign.id}
													pricingCampaign={pricingCampaign}
													onSelect={handleSelectPricingCampaign}
												/>
											))}
										</VirtualizedVirtualizer>
									</CommandGroup>
								)}
							</CommandList>
						</Virtualized>
					</Command>
				</PopoverContent>
			</Popover>
			<Tooltip>
				<TooltipTrigger asChild>
					<EditPricingCampaign pricingCampaign={selectedPricingCampaign}>
						<InputBaseAdornmentButton
							data-testid='scenario-toolbar-edit'
							disabled={
								!selectedPricingCampaign || selectedGlobalPcSet?.is_main
							}
						>
							<PenIcon className='size-4 text-muted-foreground' />
						</InputBaseAdornmentButton>
					</EditPricingCampaign>
				</TooltipTrigger>
				<TooltipContent>{t('pc.edit.tooltip')}</TooltipContent>
			</Tooltip>
			<Tooltip>
				<TooltipTrigger asChild>
					<InputBaseAdornmentButton
						data-testid='scenario-toolbar-remove'
						onClick={handleRemovePanel}
					>
						<XIcon className='size-4 text-muted-foreground' />
					</InputBaseAdornmentButton>
				</TooltipTrigger>
				<TooltipContent>{t('pc.close.tooltip')}</TooltipContent>
			</Tooltip>
		</InputBase>
	)
}
const PricingCampaignItem = ({
	pricingCampaign: { global_pc_set_id, name, id },
	onSelect,
}: {
	pricingCampaign: PricingCampaignModel
	onSelect: (pricingCampaignId: string) => void
}) => {
	const { globalPcSets } = useGlobalPcSets()
	const globalPcSet = find(propEq('id', global_pc_set_id), globalPcSets)
	const isMain = globalPcSet?.is_main
	const optionLabel = `${globalPcSet?.name} - ${name}`
	const itemValue = `${globalPcSet?.id}-${id}`

	return (
		<CommandItem
			data-value={itemValue}
			value={itemValue}
			key={itemValue}
			keywords={[optionLabel]}
			onSelect={() => onSelect(id)}
		>
			<span className='flex min-w-0 flex-1 items-center gap-2'>
				<span className='truncate'>{optionLabel}</span>
				{isMain && <CheckWaveIcon />}
			</span>
		</CommandItem>
	)
}
